import { Component, computed, effect, inject, input, output, Type, untracked } from '@angular/core';
import { ILessonListItem } from '@lb/api/mylb/interfaces';
import { IUserSummary } from '@lb/api/mylb/interfaces/iuser-summary.interface';
import { AttendanceService } from '@lb/api/mylb/services';
import { LessonService } from '@lb/api/mylb/services/lesson.service';
import { IMDMenuOption, MDMenuComponent } from '@melodic-ui/components/menu/menu.component';
import { MDContentBoxComponent, MDDialogService, MDIconComponent, MDStatusIconComponent, MDTagComponent, MDToolTipDirective } from '@melodic-ui/components';
import { TzDatePipe, TzTimePipe } from '../../pipes/timezone-aware-date.pipe';
import { buildLessonTags } from '../lessons/utils/lesson-tags.util';
import { canRescheduleLesson, isLessonEditable } from '../lessons/utils/lesson-helper.util';
import { getLessonTypeVariant } from '../lessons/utils/lesson-type-colors.util';
import { getSemesterMilestoneIcon } from '../lessons/utils/semester-milestone-config';
import { RescheduleLessonDialogService } from '@lb/services/reschedule-lesson-dialog.service';
import { SubstituteInstructorDialogService } from '@lb/components/substitute-instructor-dialog/substitute-instructor-dialog.service';
import { ConfirmationDialogComponent } from '@lb/components/confirmation-dialog/confirmation-dialog.component';
import { AuthenticationService } from '../../auth/services/authentication.service';
import { AccountStateService } from '@lb/state/account-state.service';
import { UserStateService } from '@lb/state/user-state.service';
import { GlobalTimezoneService } from '../../services/global-timezone.service';
import { UserIconComponent } from '../user-icon/user-icon.component';
import { take } from 'rxjs';
import { CommonModule } from '@angular/common';
import { DividerComponent } from '../divider/divider.component';
import { APP_CONFIG } from '@lb/config/interfaces/iapp-settings.interface';
import { usesLessonDetailsV2 } from '@lb/functions';
import { SurveyDialogComponent } from '@lb/components/lesson-detail/survey-dialog/survey-dialog.component';
import { SurveyActionComponent } from '@lb/components/lesson-detail/survey-action/survey-action.component';
import { IGradingTemplateSurveyDetail } from '@lb/api/core/interfaces/igrading-template.interface';
import { GradeTagComponent } from '@lb/components/grade-tag/grade-tag.component';
import { IndicatorType } from '@melodic-ui/types';
import { LessonSummaryDialogComponent } from './lesson-summary-dialog/lesson-summary-dialog.component';
import { LessonDropdownsComponent } from '@lb/components/lesson-dropdowns/lesson-dropdowns.component';
import { Role } from '@lb/enums/role.enum';
import { LessonStatus } from '@lb/api/mylb/enums';
import { ANALYTICS_APP, LessonMetadataService } from '@lb/services';
import { buildAdminUrl } from '@lb/config/utils/admin-url.util';

export interface ILessonCardGrade {
	categoryName: string;
	gradeName: string;
	gradeColor: IndicatorType;
}

export interface ILessonCardSurvey {
	surveyID: number;
	surveyName: string;
	isSubmitted: boolean;
	isAvailable: boolean;
	canSubmit: boolean;
	surveyResponseID: number | null;
	surveyDetail: IGradingTemplateSurveyDetail;
}

@Component({
	selector: 'lb-lesson-card-v2',
	standalone: true,
	imports: [
		CommonModule,
		MDContentBoxComponent,
		MDIconComponent,
		MDStatusIconComponent,
		MDTagComponent,
		TzDatePipe,
		TzTimePipe,
		UserIconComponent,
		DividerComponent,
		MDMenuComponent,
		GradeTagComponent,
		SurveyActionComponent,
		MDToolTipDirective,
		LessonDropdownsComponent
	],
	templateUrl: './lesson-card-v2.component.html',
	styleUrl: './lesson-card-v2.component.scss'
})
export class LessonCardV2Component {
	private readonly _authService = inject(AuthenticationService);
	private readonly _lessonService = inject(LessonService);
	private readonly _attendanceService = inject(AttendanceService);
	private readonly _dialogService = inject(MDDialogService);
	private readonly _rescheduleLessonDialogService = inject(RescheduleLessonDialogService);
	private readonly _substituteInstructorDialogService = inject(SubstituteInstructorDialogService);
	private readonly _accountStateService = inject(AccountStateService);
	private readonly _userStateService = inject(UserStateService);
	private readonly _lessonMetadataService = inject(LessonMetadataService);
	private readonly _effectiveTimezone = inject(GlobalTimezoneService).effectiveTimezone;
	private readonly _appConfig = inject(APP_CONFIG);
	// Which app this build is running in ('learner' | 'instructor' | 'admin'). Role alone isn't an
	// app check (an admin account can end up inside the learner/instructor apps), and the user row
	// must only link to admin detail pages from the admin app itself.
	private readonly _app = inject(ANALYTICS_APP, { optional: true });

	lesson = input.required<ILessonListItem>();
	lessonNumber = input<number | null>(null);
	userView = input<'learner' | 'instructor'>('learner');
	/** Which user (learner or instructor) to display in the user info row */
	displayedUser = input<'learner' | 'instructor'>('instructor');
	highlight = input<'current' | 'none'>('none');
	/** Set to false to disable card click navigation (e.g. when already on the lesson detail page) */
	clickable = input<boolean>(true);
	/** When true, applies a scroll-active ring to indicate this card is currently in view */
	isScrollActive = input<boolean>(false);
	/** Optional survey items displayed at the bottom of the card when surveys are defined for this lesson type */
	surveys = input<ILessonCardSurvey[]>([]);
	showActions = input<boolean>(true);
	showLocked = input<boolean>(false);
	isCurrent = input<boolean>(false);

	cardClick = output<number>();
	dataChanged = output<void>();

	private readonly _now = new Date();

	/**
	 * Whether the user info row links to the displayed person's admin detail page. Admin app only —
	 * instructors and learners never get the link, and neither does an admin account viewing the
	 * learner/instructor apps (the admin routes don't exist there).
	 */
	canOpenUserDetail = computed(() => this._app === 'admin' && this._accountStateService.isAdmin() && !!this.lessonUser().userID);
	pendingReschedule = computed(() => this.lesson().lessonStatusID === 3);
	awaitingPayment = computed(() => this.lesson().lessonStatusID === 4);

	cardType = computed<IndicatorType>(() => {
		if (this.isCurrent()) {
			return 'info';
		}

		return this.hasError() ? 'error' : this.hasWarning() ? 'warning' : 'default';
	});

	// Re-create Date objects when timezone changes to force TzTimePipe re-evaluation
	lessonDate = computed(() => {
		this._effectiveTimezone();
		return new Date(this.lesson().lessonDate);
	});

	lessonEndTime = computed(() => {
		this._effectiveTimezone();
		return new Date(this.lesson().lessonEndDate);
	});

	learnerSummary = computed<IUserSummary | null>(() => this._userStateService.getUserSummary(this.lesson().learnerUserID)());

	instructorSummary = computed<IUserSummary | null>(() => {
		const userID = this.lesson().instructorUserID;
		return userID ? this._userStateService.getUserSummary(userID)() : null;
	});

	lessonUser = computed(() => {
		const lesson = this.lesson();
		const summary = this.displayedUser() === 'learner' ? this.learnerSummary() : this.instructorSummary();
		const userID = this.displayedUser() === 'learner' ? lesson.learnerUserID : lesson.instructorUserID;
		// The lesson list resolves names through the lesson's own history row (so substitutes come back
		// correctly) — fall back to it when the viewer's access scope can't fetch the user summary.
		const inlineName = this.displayedUser() === 'learner' ? lesson.learnerFullName : lesson.instructorFullName;
		return {
			userID,
			role: this.displayedUser() === 'instructor' ? 'Instructor' : 'Learner',
			fullName: summary ? `${summary.firstName} ${summary.lastName}`.trim() : inlineName || undefined
		};
	});

	tags = computed(() =>
		buildLessonTags({
			lesson: this.lesson(),
			view: this.userView(),
			referenceTime: this._now
		})
	);

	statusTags = computed(() => this.tags().tags);
	hasError = computed(() => this.tags().hasError);
	hasWarning = computed(() => this.tags().hasWarning);
	// Room lessons record attendance per member, so a single session-level Present/Absent chip is
	// misleading there — shown only for single-attendance lessons. Same room test as canRescheduleLesson.
	showAttendanceTag = computed(() => {
		const lesson = this.lesson();
		const isRoomLesson = lesson.isMultiAttendance || getLessonTypeVariant(lesson.lessonTypeID) === 'chirp';
		return !isRoomLesson && lesson.attendanceStatusID != null;
	});
	semesterLessonTypeTag = computed<string | null>(() => {
		const semesterType = this.lesson().semesterLessonTypeID;
		if (!semesterType) {
			return null;
		}

		// Not standard
		if (semesterType !== 3) {
			return this.lesson().semesterLessonTypeName ?? null;
		}

		return null;
	});

	semesterMilestoneIcon = computed<string | null>(() => {
		const semesterType = this.lesson().semesterLessonTypeID;
		if (!semesterType) {
			return null;
		}
		return getSemesterMilestoneIcon(semesterType);
	});

	canReschedule = computed(() => canRescheduleLesson(this.userView(), this.lesson(), this._now));
	lessonTypeIcon = computed(() => this.getLessonTypeIcon(this.lesson().lessonTypeID));

	showDetails = computed(() => usesLessonDetailsV2(this.lesson(), this._appConfig) && this.clickable());

	/**
	 * Whether the instructor's write-up is finished, i.e. the lesson was actually taught. Mirrors
	 * `lessonWorkVisible` on the learner detail page — instructors can pre-populate a lesson and save
	 * it as a draft, so the presence of grade rows no longer means the lesson happened.
	 */
	private readonly lessonWorkVisible = computed(() => this.isAttendanceSet() || this.lesson().lessonStatusID === LessonStatus.Completed);

	/**
	 * Grade chips (and the summary button beside them). Gated so a learner never sees draft grades on
	 * a lesson that has not been taught yet; staff see their own drafts. Keyed on the viewer's role
	 * rather than the `userView` input, so a call site passing the wrong view can't leak the data.
	 */
	showGrades = computed(() => (this.lesson().grades?.length ?? 0) > 0 && (this.lessonWorkVisible() || this.isStaffViewer()));
	showSurveys = computed(() => this.surveys().length > 0);

	/** Instructor/admin by role — decides what the viewer may SEE (e.g. draft grades), not what they may change. */
	private readonly isStaffViewer = computed(() => {
		const roleID = this._authService.getUser()?.roleID;
		return roleID === Role.INSTRUCTOR || roleID === Role.ADMIN;
	});

	// Instructor/admin can change the lesson type inline, mirroring the v1 card's projected dropdowns —
	// except on a lesson from another instructor's era (predecessor or substitute), which is view-only.
	canEditLesson = computed(() => this.isStaffViewer() && isLessonEditable(this.lesson()));
	showDropdowns = computed(() => this.showActions() && this.canEditLesson());
	needsToLogLesson = computed(() => this.canEditLesson() && !this.isAttendanceSet());
	// detailsButtonLabel = computed(() => (this.canEditLesson() && !this.isAttendanceSet() ? 'Log Lesson' : 'View Lesson'));

	lessonTypeOptions = computed(() => this._lessonMetadataService.getLessonTypeOptions(this.lesson().programID)());
	// Only offer the dropdown when there's actually more than one type to choose from;
	// otherwise the static lesson-type chip is shown (nothing to select).
	showTypeDropdown = computed(() => this.showDropdowns() && this.lessonTypeOptions().length > 1);

	menuOptions = computed<IMDMenuOption[]>(() => {
		const options: IMDMenuOption[] = [];

		if (this._authService.hasEntitlement('Admin:App:Access')) {
			if (
				this.lesson().lessonStatusID !== 5 &&
				(this.lesson().lessonTypeID === 4 || this._authService.hasEntitlement('Admin:Lesson:InstructorOverride'))
			) {
				options.push({ label: 'Change Instructor', icon: '', click: () => this.openSubstituteInstructorDialog() });
			}
			if (!this.canReschedule()) {
				options.push({ label: 'Reschedule (Override)', icon: 'untitled-ui/time/clock-refresh', click: () => this.reschedule() });
			}
			options.push({ label: 'Delete Lesson', icon: '', click: () => this.deleteLesson() });
			if (this.isAttendanceSet()) {
				options.push({ label: 'Reset Attendance', icon: '', click: () => this.resetAttendance() });
			}
		}

		return options;
	});

	isAttendanceSet(): boolean {
		return !!this.lesson()?.attendanceStatusID;
	}

	onClick(): void {
		if (!this.clickable()) return;
		this.cardClick.emit(this.lesson().lessonID);
	}

	reschedule(): void {
		this._rescheduleLessonDialogService
			.open(this.lesson())
			.pipe(take(1))
			.subscribe((result) => {
				if (result) this.dataChanged.emit();
			});
	}

	resetAttendance(): void {
		const dialogRef = this._dialogService.open(ConfirmationDialogComponent as Type<Component>, {
			title: 'Reset Attendance',
			body: 'Are you sure you want to reset attendance for this lesson?',
			icon: 'untitled-ui/general/check-done-02',
			successText: 'Reset',
			action: () => this._attendanceService.resetAttendance(this.lesson().lessonID)
		});
		dialogRef.afterClosed.subscribe({
			next: (confirmed) => {
				if (confirmed) this.dataChanged.emit();
			}
		});
	}

	openSubstituteInstructorDialog(): void {
		this._substituteInstructorDialogService
			.openByEnrollmentId(this.lesson().enrollmentID, [this.lesson().lessonID], {
				userID: this.lesson().instructorUserID,
				fullName: this.lesson().instructorFullName
			})
			.pipe(take(1))
			.subscribe({
				next: (result) => {
					if (result) this.dataChanged.emit();
				}
			});
	}

	deleteLesson(): void {
		const dialogRef = this._dialogService.open(ConfirmationDialogComponent as Type<Component>, {
			title: 'Delete Lesson',
			body: 'Are you sure you want to delete this lesson? This action cannot be undone.',
			icon: 'untitled-ui/general/check-done-02',
			successText: 'Delete',
			action: () => this._lessonService.deleteLesson(this.lesson().lessonID)
		});
		dialogRef.afterClosed.subscribe({
			next: (confirmed) => {
				if (confirmed) this.dataChanged.emit();
			}
		});
	}

	constructor() {
		// Load user summaries when lesson input changes.
		// untracked() prevents the signal reads inside loadUserSummaries from being
		// registered as dependencies of this effect, avoiding an O(N²) cascade on
		// large lesson lists when the store updates.
		effect(() => {
			const lesson = this.lesson();
			const userIDs = [lesson.learnerUserID, lesson.instructorUserID].filter((id): id is number => id != null);
			untracked(() => this._userStateService.loadUserSummaries(userIDs));
		});

		// When this card can edit lessons (instructor/admin), load the program's lesson-type metadata
		// so we know whether to render the type dropdown or the static chip. Cached per program, so
		// all cards in a list share a single request.
		effect(() => {
			if (!this.showDropdowns()) return;
			const programID = this.lesson().programID;
			untracked(() => this._lessonMetadataService.initializeForProgram(programID));
		});
	}

	openSurveyDialog(survey: ILessonCardSurvey): void {
		const ref = this._dialogService.open(SurveyDialogComponent as any, {
			survey: survey.surveyDetail,
			lessonID: this.lesson().lessonID,
			enrollmentID: this.lesson().enrollmentID,
			responseID: survey.surveyResponseID,
			readonly: survey.isSubmitted
		});

		if (!survey.isSubmitted) {
			ref.afterClosed.pipe(take(1)).subscribe((result) => {
				if (result) this.dataChanged.emit();
			});
		}
	}

	openSummaryDialog(): void {
		const dialogRef = this._dialogService.open(LessonSummaryDialogComponent as Type<Component>, {
			lesson: this.lesson()
		});

		dialogRef.afterClosed.pipe(take(1)).subscribe((viewDetails) => {
			if (viewDetails) {
				this.onClick();
			}
		});
	}

	/** Opens the displayed person's admin detail page in a new tab, as the enrollment overview's user row does. */
	openUserDetail(): void {
		if (!this.canOpenUserDetail()) return;

		const url = buildAdminUrl(this._appConfig.adminAppUrl, `${this.displayedUser()}/${this.lessonUser().userID}/basic-information`);
		window.open(url, '_blank', 'noopener,noreferrer');
	}

	getLessonTypeIcon(lessonTypeID: number): string {
		switch (lessonTypeID) {
			case 1: // Mastery
				return 'untitled-ui/general/target-05';
			case 2: // Instructional
				return 'untitled-ui/media/monitor-01';
			case 3: // Tutoring
				return 'untitled-ui/communication/message-circle-01';
			case 4: // Chirp Room
				return 'mylb/bird-outline';
			default:
				return 'untitled-ui/education/book-open-01';
		}
	}
}
